import { LitElement } from 'lit';
import { property, state } from 'lit/decorators.js';
import { html } from 'lit/html.js';
import { ThemeMixin } from '@cof/dxo-ui-theme';
import { DxoStylesMobileMenu } from './DxoStylesMobileMenu.js';
import { DxoStyles } from '../DxoStyles.js';
import { backArrowIcon, chevronDownIcon } from '../icons.js';
import { Authentication, MobileMenuSection } from '../DxoUiHeader.js';
import { getLogout, getProfilePicture, isActiveHeaderTab, isActiveSubheaderDropdownLink } from '../utils.js';

export class DxoUiHeaderMobileMenu extends ThemeMixin(LitElement) {
  static styles = [DxoStyles, DxoStylesMobileMenu];

  @property({ type: Array }) mobilemenucontent?: MobileMenuSection[];

  @property({ type: Object }) authentication!: Authentication;

  @property({ type: String, attribute: 'active-tab' }) activeTab = '';

  @property({ type: String, attribute: 'active-sub-tab' }) activeSubTab = '';

  @property({ type: String, attribute: 'active-sub-tab-link' }) activeSubTabLink = '';

  @property({ type: String }) activeMobileMenuDropdownLink = '';

  @property({ type: Boolean }) closing = false;

  @property({ type: Number }) openTabIndex: number = -1;

  @property({ type: Number }) openSubTabIndex: number = -1;

  @state() expandedCollapsibles: Set<number> = new Set();

  // determine if level 3 (of mobilemenucontent) link is active
  isActiveSubTabLink(parentLabel: string, subTabLabel: string, linkLabel: string): string {
    if (!this.activeTab || this.activeTab.toLowerCase() !== parentLabel.toLowerCase()) return '';
    return isActiveSubheaderDropdownLink(this.activeSubTab, this.activeSubTabLink, subTabLabel, linkLabel);
  }

  // determine if level 4 (of mobilemenucontent) link is active
  isActiveDropdownLink(parentLabel: string, subTabLabel: string, linkLabel: string, childLabel: string): string {
    if (this.isActiveSubTabLink(parentLabel, subTabLabel, linkLabel) !== 'active') return '';
    return this.activeMobileMenuDropdownLink && childLabel.toLowerCase() === this.activeMobileMenuDropdownLink.toLowerCase() ? 'active' : '';
  }

  handleLinkClick(e: MouseEvent) {
    const target = e.target as HTMLElement;
    if (target.closest('a')) {
      this.dispatchEvent(new CustomEvent('mobile-menu-link-click', { bubbles: true, composed: true }));
    }
  }

  getProfileHeader() {
    return html`
      <a href="/dashboard/user-settings" class="logout-dropdown-header mobile">
        ${getProfilePicture(this.authentication)}
        <p class="logout-dropdown-name">${this.authentication.displayName}</p>
      </a>
    `;
  }

  getMobileMainMenu() {
    return html`
      ${this.mobilemenucontent?.map((tab, tabIndex) => html`
        ${tab.children?.length ? html`
          <div class="mobile-tab-group">
            <a href=${tab.link} class="mobile-header-tab ${isActiveHeaderTab(this.activeTab, tab.label)}">${tab.label}</a>
            <div class="mobile-sub-tabs">
              ${tab.children.map((subTab, subTabIndex) => html`
                ${subTab.children?.length ? html`
                  <button class="mobile-sub-tab" @click=${() => { this.openTabIndex = tabIndex; this.openSubTabIndex = subTabIndex; }}>
                    <span>${subTab.label}</span>
                  </button>
                ` : html`
                  <a href=${subTab.link} class="mobile-sub-tab"><span>${subTab.label}</span></a>
                `}
              `)}
            </div>
          </div>
        ` : html`
          <a href=${tab.link} class="mobile-header-tab ${isActiveHeaderTab(this.activeTab, tab.label)}">${tab.label}</a>
        `}
      `)}
    `;
  }

  handleBackClick() {
    this.openTabIndex = -1;
    this.openSubTabIndex = -1;
    this.expandedCollapsibles = new Set();
  }

  toggleCollapsible(index: number) {
    const updated = new Set(this.expandedCollapsibles);
    if (updated.has(index)) {
      updated.delete(index);
    } else {
      updated.add(index);
    }
    this.expandedCollapsibles = updated;
  }

  getMobileSubmenu() {
    const parentTab = this.mobilemenucontent?.[this.openTabIndex];
    const subTab = parentTab?.children?.[this.openSubTabIndex];
    if (!subTab) return html``;
    return html`
      <button class="mobile-submenu-back" @click=${this.handleBackClick}>
        ${backArrowIcon}
        <span>Back to ${parentTab.label}</span>
      </button>
      <div class="mobile-tab-group">
        <p class="mobile-header-tab submenu">${subTab.label}</p>
        <div class="mobile-sub-tabs">
          ${subTab.children?.map((link, linkIndex) => link.children?.length ? html`
            <button 
              class="mobile-collapsible-toggle ${this.expandedCollapsibles.has(linkIndex) ? 'expanded' : ''}" 
              @click=${() => this.toggleCollapsible(linkIndex)}
              aria-expanded=${this.expandedCollapsibles.has(linkIndex)}
            >
              <span>${link.label}</span>
              <span class="mobile-collapsible-chevron ${this.expandedCollapsibles.has(linkIndex) ? 'expanded' : ''}">${chevronDownIcon}</span>
            </button>
            <div class="mobile-collapsible-content ${this.expandedCollapsibles.has(linkIndex) ? 'expanded' : ''}">
              <div>
                ${link.children.map(child => html`
                  <a href=${child.link} class="mobile-sub-tab submenu ${this.isActiveDropdownLink(parentTab.label, subTab.label, link.label, child.label)}"><span>${child.label}</span></a>
                `)}
              </div>
            </div>
          ` : html`
            <a href=${link.link} class="mobile-sub-tab submenu ${this.isActiveSubTabLink(parentTab.label, subTab.label, link.label)}"><span>${link.label}</span></a>
          `)}
        </div>
      </div>
    `;
  }

  getMobileAuthMenu() {
    return html`
      ${this.getProfileHeader()}
      ${getLogout(this.authentication, true)}
    `;
  }

  getMobileSignIn() {
    return html`
      ${typeof(this.authentication.login) === 'function' ?
        html`<button class="mobile-menu-profile" @click=${this.authentication.login}>
          <span class="mobile-menu-profile-text">Sign In ➔</span>
        </button>`
        :
        html`<a class="mobile-menu-profile" href="${this.authentication.login}">
          <span class="mobile-menu-profile-text">Sign In ➔</span>
        </a>`
      }
    `;
  }

  render() {
    return html`
      <div class="mobile-menu-overlay ${this.closing ? 'closing' : ''}"}></div>
      <div class="default-mobile-menu ${this.closing ? 'closing' : ''}" @click=${this.handleLinkClick}>
        <div class="mobile-menu-section ${this.openTabIndex >= 0 ? 'submenu' : ''}">
          ${this.openTabIndex < 0 ? this.getMobileMainMenu() : this.getMobileSubmenu()}
        </div>
        <div class="mobile-menu-auth">
          ${this.authentication.authenticated ? this.getMobileAuthMenu() : this.getMobileSignIn()}
        </div>
      </div>
    `;
  }
}
